/**
 * Per-locale fonts (docs/I18N.md "Fonts"). Latin locales use the bundled Fredoka / Lexend / Patrick Hand stacks.
 * CJK locales add a Noto Sans face from @fontsource, loaded lazily (CSS + unicode-range woff2 slices, so the browser
 * downloads only the glyphs a screen uses; the slices are runtime-cached by the service worker, not precached).
 * The font stacks per script live in styles/i18n.css (`:root[data-script='cjk-sc']` …).
 *   zh-Hans → Noto Sans SC (shipped) · zh-Hant → Noto Sans TC · ja → Noto Sans JP · ko → Noto Sans KR (later waves:
 *   add the @fontsource package and a loader line here).
 */
import { LOCALES, type Locale } from '@sketchling/core';

const LOADERS: Partial<Record<string, () => Promise<unknown>>> = {
  'cjk-sc': () => Promise.all([import('@fontsource/noto-sans-sc/400.css'), import('@fontsource/noto-sans-sc/700.css')]),
};
const started = new Map<string, Promise<unknown>>();

export function loadCjkFont(l: Locale): Promise<unknown> {
  const script = LOCALES[l]?.script ?? 'latin';
  const load = LOADERS[script];
  if (!load) return Promise.resolve();
  let p = started.get(script);
  if (!p) started.set(script, (p = load().catch(() => undefined)));
  return p;
}

/** Canvas font fallbacks (share cards, PDF pages): the CJK face after the Latin one. */
export const CANVAS_CJK_FALLBACK = "'Noto Sans SC', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif";
